// Reports board — MEEVO / ME REPORTS / OTHER, then a left menu of names
// with no clinic code. Right side lists files from that clinic's Drive
// folder of the same name. Stats stay empty until we extract them (INV-4).
//
// N-071 (owner, 2026-10-11): every file opens in Google (never a download,
// N-073); "Add a file" puts a new or corrected report in the folder (manager:
// own clinic); "Move to Old reports" takes one off the list into the folder's
// "Old reports" — owner/director only, never a trash (src/report_files.js).
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { authedGet } from '../lib/apiClient';
import { session } from '../lib/session';
import {
  canAddReportFiles, canMoveReportFiles, moveReportFileToOld, uploadReportFolderFile,
} from '../lib/reportFile';
import { webLink } from '../lib/webLink';
import { formatDate } from '../lib/date';
import type { LocationCode } from '../lib/locations';
import {
  REPORT_GROUPS,
  reportFavoriteKey,
  reportFolderName,
  reportFolderResourceKey,
  reportLabelFromKey,
  reportsForGroup,
  type ReportGroup,
} from '../lib/meevoReports';
import { refetchResource, useResource } from '../lib/store';
import { EmptyState, FaultState } from './FaultState';
import { FavoriteStar } from './FavoriteStar';

export interface ReportFolderFile {
  id: string;
  name: string;
  webViewLink: string | null;
  modifiedTime: string | null;
}

export interface ReportFolderData {
  configured: boolean;
  folderName: string | null;
  folderUrl?: string | null;
  /** The folder's "Old reports" child, when one exists (N-071). */
  oldFolderUrl?: string | null;
  files: ReportFolderFile[];
  reason?: string;
}

function loadReportFolder(clinic: string, group: string, label: string): Promise<ReportFolderData> {
  const q = new URLSearchParams({ location: clinic, group, label });
  return authedGet<ReportFolderData>(`/api/v1/reports/folder?${q.toString()}`);
}

/** One file's row: Open in Google, and — owner/director — Move to Old
 * reports behind a one-step confirm. */
function ReportFileRow({ file, canMove, onMove }: {
  file: ReportFolderFile;
  canMove: boolean;
  onMove: (file: ReportFolderFile) => Promise<void>;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const href = webLink(file.webViewLink);
  async function confirmMove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onMove(file);
      setConfirming(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <tr data-testid={`report-file-${file.id}`}>
      <td>{file.name}</td>
      <td>{file.modifiedTime ? formatDate(file.modifiedTime) : '—'}</td>
      <td>
        {href ? (
          <a href={href} target="_blank" rel="noreferrer">Open</a>
        ) : '—'}
      </td>
      {canMove ? (
        <td>
          {confirming ? (
            <span data-testid={`report-file-${file.id}-confirm`}>
              Move to Old reports? It stays in Drive.{' '}
              <button type="button" class="btn" disabled={busy} onClick={() => { void confirmMove(); }}>
                {busy ? 'Moving…' : 'Yes'}
              </button>{' '}
              <button type="button" class="btn" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
            </span>
          ) : (
            <button
              type="button" class="btn"
              data-testid={`report-file-${file.id}-move`}
              onClick={() => setConfirming(true)}
            >
              Move to Old reports
            </button>
          )}
          {error ? <small class="bad" role="alert">{error}</small> : null}
        </td>
      ) : null}
    </tr>
  );
}

function ReportFiles({ data, label, clinic, group, onChanged }: {
  data: ReportFolderData;
  label: string;
  clinic: LocationCode;
  group?: ReportGroup;
  onChanged?: () => Promise<unknown>;
}): JSX.Element {
  const expected = reportFolderName(clinic, label);
  const folderName = data.folderName || expected;
  const me = session.value.me;
  const target = { clinic, group: group ?? 'MEEVO', label };
  const canAdd = Boolean(onChanged) && data.configured && Boolean(data.folderUrl) && canAddReportFiles(me, clinic);
  const canMove = Boolean(onChanged) && canMoveReportFiles(me);
  const picker = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function onPick(ev: Event): Promise<void> {
    const input = ev.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    setUploading(true);
    setMessage(null);
    setUploadError(null);
    try {
      const out = await uploadReportFolderFile(target, file);
      setMessage(`Added ${out.file.name}.`);
      await onChanged?.();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  async function moveToOld(file: ReportFolderFile): Promise<void> {
    await moveReportFileToOld(target, file.id);
    setMessage(`Moved ${file.name} to Old reports.`);
    await onChanged?.();
  }

  const oldHref = webLink(data.oldFolderUrl);
  return (
    <>
      <div class="section-head">
        <h2>{label}</h2>
        <p>
          Folder: {folderName}
          {data.folderUrl ? (
            <>
              {' · '}
              <a href={data.folderUrl} target="_blank" rel="noreferrer" data-testid="report-folder-link">
                Open folder
              </a>
            </>
          ) : null}
          {oldHref ? (
            <>
              {' · '}
              <a href={oldHref} target="_blank" rel="noreferrer" data-testid="report-old-folder-link">
                Old reports
              </a>
            </>
          ) : null}
        </p>
      </div>
      {canAdd ? (
        <div class="money-add">
          <input
            ref={picker}
            type="file"
            hidden
            data-testid="report-file-input"
            onChange={(ev) => { void onPick(ev); }}
          />
          <button
            type="button" class="btn"
            disabled={uploading}
            data-testid="report-file-add"
            onClick={() => picker.current?.click()}
          >
            {uploading ? 'Adding…' : 'Add a file'}
          </button>
        </div>
      ) : null}
      {message ? <p class="ok" role="status">{message}</p> : null}
      {uploadError ? <p class="bad" role="alert">{uploadError}</p> : null}
      {!data.configured ? (
        <EmptyState label="Drive is not configured for report folders." />
      ) : data.files.length === 0 ? (
        <EmptyState label={data.reason === 'report folder not found'
          ? `No Drive folder named ${expected} yet.`
          : 'No files in this folder yet.'}
        />
      ) : (
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>File</th>
                <th>Date</th>
                <th>Open</th>
                {canMove ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {data.files.map((file) => (
                <ReportFileRow key={file.id} file={file} canMove={canMove} onMove={moveToOld} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div class="section-head report-stats-head">
        <h2>Stats</h2>
        <p>Numbers taken from this report, once we extract them.</p>
      </div>
      <EmptyState label="Stats from this report will show here once we extract them." />
    </>
  );
}

export function ReportBoard({ clinic }: { clinic: LocationCode }): JSX.Element {
  const [group, setGroup] = useState<ReportGroup>('MEEVO');
  const [picked, setPicked] = useState('');
  const items = reportsForGroup(group);
  const label = items.includes(picked) ? picked : '';
  const folderKey = label ? reportFolderResourceKey(clinic, group, label) : null;
  const folder = useResource<ReportFolderData>(
    folderKey,
    () => loadReportFolder(clinic, group, label),
  );

  useEffect(() => {
    const applyHash = (): void => {
      const name = reportLabelFromKey(window.location.hash);
      if (!name) return;
      setGroup('MEEVO');
      setPicked(name);
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  return (
    <>
      <div class="cadence-tabs" role="tablist" aria-label="Report group">
        {REPORT_GROUPS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={group === id}
            class={group === id ? 'tab active' : 'tab'}
            data-testid={`report-group-${id}`}
            onClick={() => { setGroup(id); setPicked(''); }}
          >
            {id}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState label="No reports in this group yet." />
      ) : (
        <div class="report-board">
          <nav class="report-menu" aria-label="Reports">
            {items.map((name) => (
              <div key={name} class="report-menu-row">
                <button
                  type="button"
                  class={name === label ? 'report-menu-item active' : 'report-menu-item'}
                  data-testid={`report-item-${name}`}
                  onClick={() => setPicked(name)}
                >
                  {name}
                </button>
                <FavoriteStar itemKey={reportFavoriteKey(name)} />
              </div>
            ))}
          </nav>
          <div class="report-stage">
            {!label ? (
              <EmptyState label="Click a report to load its folder." />
            ) : folder.status === 'loading' ? (
              <EmptyState label="Loading folder…" />
            ) : folder.status === 'fault' ? (
              <FaultState name="report folder" detail={folder.error} />
            ) : (
              <ReportFiles
                data={folder.data}
                label={label}
                clinic={clinic}
                group={group}
                onChanged={() => refetchResource(folderKey!, () => loadReportFolder(clinic, group, label))}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
